import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { Button, Input, Modal, ModalProps, Stack, useModals, useToasts } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface SaveWidgetModalProps extends ModalProps {
  onSave: (title: string) => Promise<void>;
  defaultTitle?: string;
  heading?: string;
}

export function SaveWidgetModal({ onSave, defaultTitle, heading, ...props }: SaveWidgetModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { showInfo } = useToasts();
  const [isSaving, setIsSaving] = useState(false);

  const schema = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: { title: defaultTitle ?? "" },
  });

  const onSubmit = handleSubmit(async (data) => {
    setIsSaving(true);
    try {
      await onSave(data.title);
      showInfo({ title: t("reporting:people_analytics_widget_saved") });
      close(props.id);
    } catch {
      // Ошибку показывает onError мутации; модалку оставляем открытой для повтора
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Modal
      {...props}
      title={heading ?? t("reporting:people_analytics_save_widget")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} onClick={onSubmit} isLoading={isSaving}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("reporting:people_analytics_widget_title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
